import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { TradingOffer } from '../../../types/TradingOffer';

type TradingOfferActionCardProps = {
  offer: TradingOffer;
  availableShares: number;
  purchaseQuantity: number;
  commissionRate: number;
  isExpired: boolean;
  onQuantityChange: (quantity: number) => void;
  onBuyShares: VoidFunction;
  onViewOtherOffers: VoidFunction;
};

const TradingOfferActionCard: FC<TradingOfferActionCardProps> = ({
  offer,
  availableShares,
  purchaseQuantity,
  commissionRate,
  isExpired,
  onQuantityChange,
  onBuyShares,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === 'SELL';

  const subtotal = useMemo(
    () => purchaseQuantity * offer.sharePrice,
    [purchaseQuantity, offer.sharePrice]
  );

  const commission = useMemo(
    () => subtotal * commissionRate,
    [subtotal, commissionRate]
  );

  const total = useMemo(() => subtotal + commission, [subtotal, commission]);

  const quickAmounts = useMemo(() => {
    const amounts = [];
    if (availableShares >= 10) amounts.push(10);
    if (availableShares >= 25) amounts.push(25);
    if (availableShares > 0) amounts.push(availableShares);
    return amounts;
  }, [availableShares]);

  const handleIncrement = () => {
    if (purchaseQuantity < availableShares) {
      onQuantityChange(purchaseQuantity + 1);
    }
  };

  const handleDecrement = () => {
    if (purchaseQuantity > 1) {
      onQuantityChange(purchaseQuantity - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value)) {
      onQuantityChange(value);
    }
  };

  if (isExpired) {
    return (
      <div
        className="card card-bordered"
        style={{ position: 'sticky', top: '85px' }}
      >
        <div className="card-inner text-center py-5">
          <em className="icon ni ni-clock fs-1 text-warning mb-3 d-block" />
          <h5 className="mb-2">{t('ExpiredOffer')}</h5>
          <p className="text-soft mb-3">{t('ExpiredOfferActionDescription')}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onViewOtherOffers}
          >
            {t('ViewOtherOffers')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="card card-bordered"
      style={{ position: 'sticky', top: '85px' }}
    >
      <div className="card-inner">
        <h6 className="card-title text-base">
          {isSellOffer ? t('BuyShares') : t('SellShares')}
        </h6>

        <div className="form-group mb-3">
          <label className="form-label" htmlFor="purchase-quantity">
            {t('Quantity')}
          </label>

          <div className="d-flex align-items-center gap-2 mb-2">
            {quickAmounts.map((amount) => (
              <button
                key={amount}
                type="button"
                className={`btn btn-sm ${
                  purchaseQuantity === amount
                    ? 'btn-primary'
                    : 'btn-outline-light'
                }`}
                onClick={() => onQuantityChange(amount)}
              >
                {amount === availableShares ? t('All') : formatNumber(amount)}
              </button>
            ))}
          </div>

          <div className="form-control-wrap d-flex align-items-center">
            <button
              type="button"
              className="btn btn-icon btn-outline-light"
              onClick={handleDecrement}
              disabled={purchaseQuantity <= 1}
            >
              <em className="icon ni ni-minus" />
            </button>
            <input
              id="purchase-quantity"
              type="number"
              className="form-control text-center mx-2"
              value={purchaseQuantity}
              onChange={handleInputChange}
              min={1}
              max={availableShares}
            />
            <button
              type="button"
              className="btn btn-icon btn-outline-light"
              onClick={handleIncrement}
              disabled={purchaseQuantity >= availableShares}
            >
              <em className="icon ni ni-plus" />
            </button>
          </div>

          <span className="form-note mt-1">
            {t('MaxAvailable')}: {formatNumber(availableShares)}{' '}
            {t('Shares').toLowerCase()}
          </span>
        </div>

        <div className="divider" />

        <h6 className="overline-title-alt mb-3">{t('PurchaseSummary')}</h6>

        <ul className="nk-list-plain">
          <li className="d-flex justify-content-between py-1">
            <span className="sub-text">{t('Shares')}</span>
            <span className="lead-text">{formatNumber(purchaseQuantity)}</span>
          </li>
          <li className="d-flex justify-content-between py-1">
            <span className="sub-text">{t('PricePerShare')}</span>
            <span className="lead-text">
              {formatCurrency(offer.sharePrice)}
            </span>
          </li>
          <li className="d-flex justify-content-between py-1">
            <span className="sub-text">{t('Subtotal')}</span>
            <span className="lead-text">{formatCurrency(subtotal)}</span>
          </li>
          <li className="d-flex justify-content-between py-1">
            <span className="sub-text">
              {t('Commission')} ({(commissionRate * 100).toFixed(1)}%)
            </span>
            <span className="lead-text">{formatCurrency(commission)}</span>
          </li>
        </ul>

        <div className="divider" />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <span className="fw-bold">{t('Total')}</span>
          <span className="amount h5 mb-0 text-primary">
            {formatCurrency(total)}
          </span>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-block btn-lg"
          onClick={onBuyShares}
          disabled
        >
          {isSellOffer
            ? `${t('Buy')} ${formatNumber(purchaseQuantity)} ${t(
                'Shares'
              ).toLowerCase()}`
            : `${t('Sell')} ${formatNumber(purchaseQuantity)} ${t(
                'Shares'
              ).toLowerCase()}`}
        </button>

        <button
          type="button"
          className="btn btn-link btn-block mt-2"
          onClick={onViewOtherOffers}
        >
          {t('ViewOtherOffers')}
        </button>
      </div>
    </div>
  );
};

export default TradingOfferActionCard;
